import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Bell,
  BellRing,
  CornerDownLeft,
  History,
  ListChecks,
  Loader2,
  QrCode,
  Search,
  UserRound,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { useAuth } from '@/context/AuthContext'
import { useI18n } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'
import { useDebounced } from '@/lib/useDebounced'
import { cn } from '@/lib/utils'
import type { QuickAction, VisibleNavigation } from './navigation'
import {
  flatten,
  mergePalette,
  MIN_SERVER_QUERY,
  rememberItem,
  serverGroups,
  type PaletteItem,
} from './searchModel'
import { shortcutLabel } from './shortcut'
import { loadRecent, saveRecent, useServerSearch } from './useSearch'

/*
 * Extra words per destination, so somebody who does not know a page's name
 * still finds it: "lozinka" finds the account screen, "kvar" a new report.
 */
const KEYWORDS: Record<string, TranslationKey> = {
  '/prijave/nova': 'search.keywords.submission',
  '/5s/nova': 'search.keywords.walk',
  '/zadaci?novi=1': 'search.keywords.task',
  '/obavijesti': 'search.keywords.notifications',
  '/profil#obavijesti': 'search.keywords.notificationSettings',
  '/profil': 'search.keywords.profile',
  '/skeniraj': 'search.keywords.scan',
  '/admin/postavke': 'search.keywords.settings',
  '/admin/nalepnice': 'search.keywords.badges',
  '/admin/obrasci': 'search.keywords.templates',
}

const KIND_ICON: Record<PaletteItem['kind'], LucideIcon> = {
  page: ArrowRight,
  action: ArrowRight,
  submission: ListChecks,
  workCenter: Wrench,
  person: UserRound,
  more: Search,
}

/**
 * Ctrl+K, and the header's search box.
 *
 * One list, walked with the arrow keys, mixing two kinds of answer: the pages
 * and actions this person can reach, which are known here and answer on the
 * first keystroke, and reports, machines and people from `GET /api/search`,
 * which arrive a moment later. With nothing typed it is the whole menu — the
 * recently opened, the actions, every page — so it is as useful to somebody
 * who reaches for the keyboard instead of the mouse as to somebody looking for
 * one report.
 *
 * A hand-built ARIA combobox on Base UI's Dialog, rather than Base UI's
 * Autocomplete: the rows are filtered and ordered by `searchModel.ts`, half of
 * them arrive asynchronously, and the choice is a navigation rather than a
 * value — none of which the Autocomplete's own filtering and selection
 * model is for. The pattern is the standard one: focus stays in the input,
 * `aria-activedescendant` names the highlighted row.
 */
export function SearchPalette({
  open,
  onOpenChange,
  nav,
  actions,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  nav: VisibleNavigation
  actions: QuickAction[]
}) {
  const { t, tp } = useI18n()
  const { user } = useAuth()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`

  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [recent, setRecent] = useState<PaletteItem[]>(() => loadRecent(user?.id))

  // Fresh every time it opens: last time's query is somebody else's errand.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
      setRecent(loadRecent(user?.id))
    }
  }, [open, user?.id])

  const debounced = useDebounced(query, 200)
  const server = useServerSearch(open ? debounced : '')

  /*
   * What the client knows: every page this person's navigation shows, and the
   * actions they may take. Built from the same `visibleNavigation` the rail
   * draws, so search can never offer a page the menu hides.
   */
  const { pages, actionItems, icons } = useMemo(() => {
    const icons = new Map<string, LucideIcon>()

    const entry = (
      kind: 'page' | 'action',
      path: string,
      label: string,
      icon: LucideIcon,
      hint?: string,
    ): PaletteItem => {
      icons.set(path, icon)

      return {
        id: `${kind}:${path}`,
        kind,
        label,
        hint,
        path,
        keywords: KEYWORDS[path] ? t(KEYWORDS[path]) : undefined,
      }
    }

    const adminLabel = t('nav.admin')

    const pages: PaletteItem[] = [
      ...nav.groups.flatMap((group) =>
        group.items.map((item) => entry('page', item.to, t(item.labelKey), item.icon, t(item.hintKey))),
      ),
      ...nav.admin.map((page) => entry('page', page.to, t(page.labelKey), page.icon, adminLabel)),
      ...nav.footer.map((item) => entry('page', item.to, t(item.labelKey), item.icon, t(item.hintKey))),
      entry('page', '/profil', t('account.myAccount'), UserRound),
    ]

    const actionItems: PaletteItem[] = [
      ...actions.map((action) =>
        entry('action', action.to, t(action.labelKey), action.icon, t(action.hintKey)),
      ),
      entry('action', '/obavijesti', t('search.action.notifications'), Bell),
      entry('action', '/profil#obavijesti', t('search.action.notificationSettings'), BellRing),
      entry('action', '/skeniraj', t('header.scanQr'), QrCode, t('header.scanQr.hint')),
    ]

    return { pages, actionItems, icons }
  }, [nav, actions, t])

  const groups = useMemo(
    () =>
      mergePalette({
        query,
        actions: actionItems,
        pages,
        recent,
        server: serverGroups(server.data, user, {
          more: (count) => t('search.more', { count }),
        }),
      }),
    [query, actionItems, pages, recent, server.data, user, t],
  )

  const items = flatten(groups)
  const current = Math.min(active, Math.max(items.length - 1, 0))
  const optionId = (index: number) => `${baseId}-option-${index}`

  /*
   * A new query starts at the top, so Enter opens the best match.
   *
   * Only a new query — not the server's answer arriving. Its groups come
   * after the pages, so somebody who has already arrowed down to a page keeps
   * it; and an exact reference, which the model moves to the very top, lands
   * on the row that is already highlighted.
   */
  useEffect(() => {
    setActive(0)
  }, [query])

  // Keep the highlighted row in view as the arrow keys walk past the edge.
  useEffect(() => {
    if (!open) return

    document.getElementById(`${baseId}-option-${current}`)?.scrollIntoView({ block: 'nearest' })
  }, [baseId, current, open])

  function choose(item: PaletteItem | undefined) {
    if (!item) return

    const next = rememberItem(recent, item)
    setRecent(next)
    saveRecent(user?.id, next)

    onOpenChange(false)
    navigate(item.path)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (items.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current + 1) % items.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current - 1 + items.length) % items.length)
    } else if (event.key === 'PageDown') {
      event.preventDefault()
      setActive(Math.min(current + 5, items.length - 1))
    } else if (event.key === 'PageUp') {
      event.preventDefault()
      setActive(Math.max(current - 5, 0))
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      choose(items[current])
    }
  }

  const typed = query.trim()
  const tooShort = typed.length > 0 && typed.length < MIN_SERVER_QUERY
  const nothing = typed.length >= MIN_SERVER_QUERY && items.length === 0 && !server.loading
  const canFindPeople = Boolean(user?.isManager || user?.role === 'ADMIN')

  let index = -1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        initialFocus={inputRef}
        className={cn(
          'flex max-h-[calc(100dvh-1rem)] flex-col gap-0 overflow-hidden p-0',
          // Near the top on every screen, not centred: on a phone the keyboard
          // takes the bottom half, and on a desk the list grows downward from
          // a box that stays where the eye found it.
          'top-2 max-w-[calc(100%-1rem)] translate-y-0 sm:top-[12vh] sm:max-h-[76vh] sm:max-w-xl',
          'rounded-2xl border border-black/5 dark:border-white/10',
          'supports-[backdrop-filter]:bg-popover/90 backdrop-blur-2xl backdrop-saturate-150',
        )}
      >
        <DialogTitle className="sr-only">{t('search.title')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t(canFindPeople ? 'search.placeholder.people' : 'search.placeholder')}
        </DialogDescription>

        <div className="flex items-center gap-2.5 border-b px-3.5">
          <Search className="text-muted-foreground size-4.5 shrink-0" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={items.length > 0 ? optionId(current) : undefined}
            aria-label={t('search.title')}
            placeholder={t(canFindPeople ? 'search.placeholder.people' : 'search.placeholder')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            className="placeholder:text-muted-foreground h-13 min-w-0 flex-1 bg-transparent outline-none pointer-fine:h-12"
          />
          {server.loading && (
            <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" aria-hidden />
          )}
          {/* The way out, for a touch screen with no Esc key. */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={t('search.keys.close')}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 -mr-1.5 flex size-control shrink-0 items-center justify-center rounded-lg text-xs font-medium focus-visible:ring-2 focus-visible:outline-none"
          >
            <kbd aria-hidden className="bg-muted rounded-md px-1.5 py-0.5 font-sans">
              Esc
            </kbd>
          </button>
        </div>

        {/* Polite, so the count is read after whatever the reader was typing. */}
        <p className="sr-only" role="status" aria-live="polite">
          {typed ? tp('search.results', items.length) : ''}
        </p>

        <div
          id={listId}
          role="listbox"
          aria-label={t('search.title')}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
        >
          {groups.map((group) => {
            const headingId = `${baseId}-group-${group.id}`

            return (
              <div key={group.id} role="group" aria-labelledby={headingId} className="pb-1">
                <div
                  id={headingId}
                  className="text-muted-foreground flex items-center gap-1.5 px-2 pt-2 pb-1 text-xs font-medium"
                >
                  {group.id === 'recent' && <History className="size-3.5" aria-hidden />}
                  {t(group.labelKey)}
                </div>

                {group.items.map((item) => {
                  index += 1
                  const position = index
                  const selected = position === current
                  const Icon = icons.get(item.path) ?? KIND_ICON[item.kind]

                  return (
                    <div
                      key={item.id}
                      id={optionId(position)}
                      role="option"
                      aria-selected={selected}
                      // Pointer movement highlights, so the mouse and the
                      // keyboard never disagree about which row Enter opens.
                      onMouseMove={() => {
                        if (!selected) setActive(position)
                      }}
                      onClick={() => choose(item)}
                      className={cn(
                        'flex min-h-control cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm select-none',
                        selected && 'bg-accent text-accent-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'flex size-7 shrink-0 items-center justify-center rounded-md',
                          selected ? 'bg-background/70 text-primary' : 'bg-muted text-muted-foreground',
                        )}
                        aria-hidden
                      >
                        <Icon className="size-4" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-baseline gap-2">
                          {item.badge && (
                            <span className="text-muted-foreground shrink-0 font-mono text-xs">
                              {item.badge}
                            </span>
                          )}
                          <span className={cn('truncate', item.kind === 'more' && 'text-primary font-medium')}>
                            {item.label}
                          </span>
                        </span>
                        {item.hint && (
                          <span className="text-muted-foreground block truncate text-xs">{item.hint}</span>
                        )}
                      </span>

                      {selected && (
                        <CornerDownLeft
                          className="text-muted-foreground hidden size-3.5 shrink-0 pointer-fine:block"
                          aria-hidden
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}

          {nothing && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium">{t('search.empty', { query: typed })}</p>
              <p className="text-muted-foreground mt-1 text-xs">{t('search.emptyHint')}</p>
            </div>
          )}
        </div>

        {(tooShort || server.unavailable) && (
          <p className="text-muted-foreground border-t px-4 py-2 text-xs">
            {t(server.unavailable ? 'search.unavailable' : 'search.short')}
          </p>
        )}

        {/* The keys, for a keyboard. A touch screen has none of them. */}
        <div className="text-muted-foreground hidden items-center gap-4 border-t px-4 py-2 text-[0.6875rem] pointer-fine:flex">
          <span className="flex items-center gap-1">
            <kbd className="bg-muted rounded px-1 font-sans">↑</kbd>
            <kbd className="bg-muted rounded px-1 font-sans">↓</kbd>
            {t('search.keys.move')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-muted rounded px-1 font-sans">↵</kbd>
            {t('search.keys.open')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-muted rounded px-1 font-sans">Esc</kbd>
            {t('search.keys.close')}
          </span>
          <span className="ml-auto">{shortcutLabel()}</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
